import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LayoutDashboard, Package, Receipt, ShoppingCart, Mail, LogOut, Plus, Trash2, Pencil, Upload, X, AlertTriangle, RefreshCw } from "lucide-react";
import { formatPrice } from "@/lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;
const TKEY = "orelie_admin_token";
const CATS = ["Bărbați", "Femei", "Unisex"];
const EMPTY = { name: "", brand: "ORÉLIE", category: "Unisex", price: "", stock: "", initial_stock: "", discount_pct: "", description: "", notes_top: "", notes_heart: "", notes_base: "", image: "", images: [] };

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem(TKEY)}` });

function Login({ onLogin }) {
  const [u, setU] = useState(""); const [p, setP] = useState(""); const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { username: u, password: p });
      localStorage.setItem(TKEY, data.token); onLogin();
    } catch (err) { toast.error(err.response?.data?.detail || "Autentificare eșuată"); }
    setLoading(false);
  };
  return (
    <div className="min-h-screen flex items-center justify-center bg-cream px-6">
      <form onSubmit={submit} data-testid="admin-login-form" className="w-full max-w-sm bg-surface border border-line p-8">
        <p className="font-serif text-3xl text-ink text-center">ORÉLIE</p>
        <p className="eyebrow text-center mt-1 mb-8">Panou Administrare</p>
        <label className="eyebrow text-[0.6rem] block mb-2">Utilizator</label>
        <input data-testid="admin-username" value={u} onChange={(e) => setU(e.target.value)} className="w-full bg-cream border border-line px-4 py-3 mb-4 outline-none focus:border-mocha-dark" />
        <label className="eyebrow text-[0.6rem] block mb-2">Parolă</label>
        <input data-testid="admin-password" type="password" value={p} onChange={(e) => setP(e.target.value)} className="w-full bg-cream border border-line px-4 py-3 mb-6 outline-none focus:border-mocha-dark" />
        <button data-testid="admin-login-btn" disabled={loading} className="w-full py-3.5 bg-ink text-cream text-xs uppercase tracking-[0.2em] hover:bg-mocha-dark transition-colors disabled:opacity-60">{loading ? "..." : "Autentificare"}</button>
      </form>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-surface border border-line p-6">
      <p className="eyebrow text-[0.6rem]">{label}</p>
      <p className="font-serif text-3xl text-ink mt-2">{value}</p>
    </div>
  );
}

function Dashboard() {
  const [d, setD] = useState(null);
  useEffect(() => { axios.get(`${API}/admin/dashboard`, { headers: authHeaders() }).then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return <p className="text-ink-muted">Se încarcă...</p>;
  return (
    <div data-testid="admin-dashboard" className="space-y-8">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Vânzări totale" value={formatPrice(d.total)} />
        <Stat label="Venit azi" value={formatPrice(d.daily)} />
        <Stat label="Venit luna aceasta" value={formatPrice(d.monthly)} />
        <Stat label="Comenzi online" value={d.orders_count} />
      </div>
      <div className="bg-surface border border-line p-6">
        <p className="eyebrow text-[0.6rem] mb-4">Cele mai vândute produse</p>
        {d.best_sellers.length === 0 ? <p className="text-ink-muted text-sm">Încă nu există vânzări.</p> : (
          <ul className="divide-y divide-line">
            {d.best_sellers.map((b, i) => (
              <li key={b.name} className="flex justify-between py-3"><span className="text-ink">{i + 1}. {b.name}</span><span className="text-mocha-dark font-medium">{b.qty} buc.</span></li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function statusOf(p, threshold) {
  if ((p.stock ?? 0) <= 0) return { label: "Epuizat", cls: "text-[#C1362F]" };
  if ((p.stock ?? 0) <= threshold) return { label: "Stoc redus", cls: "text-mocha-dark" };
  return { label: "Disponibil", cls: "text-emerald-700" };
}

function Products() {
  const [list, setList] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [threshold, setThreshold] = useState(3);
  const load = useCallback(() => axios.get(`${API}/products`).then((r) => setList(r.data)), []);
  const loadSettings = useCallback(() => axios.get(`${API}/admin/settings`, { headers: authHeaders() }).then((r) => setThreshold(r.data.low_stock_threshold)).catch(() => {}), []);
  useEffect(() => { load(); loadSettings(); }, [load, loadSettings]);

  const saveThreshold = async (v) => {
    setThreshold(v);
    await axios.put(`${API}/admin/settings`, { low_stock_threshold: parseInt(v) || 0 }, { headers: authHeaders() }).catch(() => {});
  };

  const reset = () => { setForm(EMPTY); setEditing(null); };
  const edit = (p) => {
    setEditing(p.id);
    const imgs = (p.images && p.images.length) ? p.images : (p.image ? [p.image] : []);
    setForm({ ...EMPTY, ...p, price: p.price ?? "", stock: p.stock ?? "", initial_stock: p.initial_stock ?? "", discount_pct: p.discount_pct ?? "", images: imgs,
      notes_top: (p.notes_top || []).join(", "), notes_heart: (p.notes_heart || []).join(", "), notes_base: (p.notes_base || []).join(", ") });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const upload = async (e) => {
    const files = Array.from(e.target.files || []); if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData(); fd.append("file", file);
        const { data } = await axios.post(`${API}/admin/upload`, fd, { headers: { ...authHeaders(), "Content-Type": "multipart/form-data" } });
        setForm((f) => ({ ...f, images: [...f.images, data.url] }));
      }
      toast.success(files.length > 1 ? "Imagini încărcate" : "Imagine încărcată");
    } catch { toast.error("Încărcare eșuată"); }
    setUploading(false);
    e.target.value = "";
  };

  const removeImage = (idx) => setForm((f) => ({ ...f, images: f.images.filter((_, i) => i !== idx) }));
  const makePrimary = (idx) => setForm((f) => { const arr = [...f.images]; const [x] = arr.splice(idx, 1); return { ...f, images: [x, ...arr] }; });

  const splitNotes = (s) => (s ? s.split(",").map((x) => x.trim()).filter(Boolean) : []);

  const save = async (e) => {
    e.preventDefault();
    if (!form.name || form.price === "") { toast.error("Nume și preț obligatorii"); return; }
    const stock = parseInt(form.stock) || 0;
    const payload = {
      name: form.name, brand: form.brand, category: form.category,
      price: parseFloat(form.price), original_price: null, discount_pct: parseInt(form.discount_pct) || 0, notes: [],
      notes_top: splitNotes(form.notes_top), notes_heart: splitNotes(form.notes_heart), notes_base: splitNotes(form.notes_base),
      description: form.description, image: form.images[0] || "", images: form.images,
      stock, initial_stock: parseInt(form.initial_stock) || stock, in_stock: stock > 0,
    };
    try {
      if (editing) await axios.put(`${API}/admin/products/${editing}`, payload, { headers: authHeaders() });
      else await axios.post(`${API}/admin/products`, payload, { headers: authHeaders() });
      toast.success(editing ? "Produs actualizat" : "Produs adăugat");
      reset(); load();
    } catch { toast.error("Eroare la salvare"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Ștergi acest parfum?")) return;
    await axios.delete(`${API}/admin/products/${id}`, { headers: authHeaders() });
    toast.success("Produs șters"); load(); if (editing === id) reset();
  };

  const inp = "border border-line bg-cream px-4 py-3 outline-none focus:border-mocha-dark";
  const soldOut = list.filter((p) => (p.stock ?? 0) <= 0);

  return (
    <div data-testid="admin-products" className="space-y-8">
      {soldOut.length > 0 && (
        <div data-testid="restock-alert" className="bg-[#FBEAE8] border border-[#E4B7B2] p-5">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle size={18} className="text-[#C1362F]" />
            <p className="font-serif text-xl text-[#C1362F]">Necesită reaprovizionare · {soldOut.length}</p>
          </div>
          <p className="text-sm text-ink-soft mb-3">Aceste parfumuri au stoc 0. Apasă pe un parfum pentru a-i actualiza stocul.</p>
          <div className="flex flex-wrap gap-2">
            {soldOut.map((p) => (
              <button key={p.id} data-testid={`restock-${p.id}`} onClick={() => edit(p)} className="text-xs bg-cream border border-[#E4B7B2] px-3 py-1.5 hover:bg-white transition-colors text-ink">
                {p.name} <span className="text-[#C1362F] font-semibold ml-1">EPUIZAT</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center gap-3 bg-surface border border-line p-4">
        <span className="text-sm text-ink-soft">Prag „Stoc redus" (1–N bucăți):</span>
        <input data-testid="low-stock-threshold" type="number" min="0" value={threshold} onChange={(e) => saveThreshold(e.target.value)} className="w-20 border border-line bg-cream px-3 py-2 outline-none focus:border-mocha-dark" />
      </div>

      <form onSubmit={save} className="bg-surface border border-line p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <p className="sm:col-span-2 font-serif text-2xl text-ink">{editing ? "Editează parfum" : "Adaugă parfum"}</p>
        <input data-testid="prod-name" placeholder="Numele parfumului" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inp} />
        <input placeholder="Brand" value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} className={inp} />
        <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={inp}>
          {CATS.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <input data-testid="prod-price" type="number" step="0.01" placeholder="Preț (RON)" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className={inp} />
        <input data-testid="prod-stock" type="number" placeholder="Stoc curent" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} className={inp} />
        <input type="number" placeholder="Stoc inițial (opțional)" value={form.initial_stock} onChange={(e) => setForm({ ...form, initial_stock: e.target.value })} className={inp} />
        <div className="sm:col-span-2 flex flex-col gap-1.5">
          <input data-testid="prod-discount" type="number" min="0" max="99" placeholder="Reducere % (0 = fără reducere)" value={form.discount_pct} onChange={(e) => setForm({ ...form, discount_pct: e.target.value })} className={inp} />
          {(() => {
            const pct = parseInt(form.discount_pct) || 0;
            const price = parseFloat(form.price) || 0;
            if (pct > 0 && pct < 100 && price > 0) {
              const orig = price / (1 - pct / 100);
              return <p data-testid="prod-discount-preview" className="text-xs text-ink-muted">Se va afișa badge <span className="font-semibold text-ink">−{pct}%</span>, preț vechi tăiat <span className="line-through">{orig.toFixed(2)} RON</span>, preț de vânzare <span className="font-semibold text-ink">{price.toFixed(2)} RON</span></p>;
            }
            return <p className="text-xs text-ink-muted">Lasă 0 pentru a nu afișa nicio reducere.</p>;
          })()}
        </div>
        <input placeholder="Note de vârf (virgulă)" value={form.notes_top} onChange={(e) => setForm({ ...form, notes_top: e.target.value })} className={`sm:col-span-2 ${inp}`} />
        <input placeholder="Note de mijloc (virgulă)" value={form.notes_heart} onChange={(e) => setForm({ ...form, notes_heart: e.target.value })} className={`sm:col-span-2 ${inp}`} />
        <input placeholder="Note de bază (virgulă)" value={form.notes_base} onChange={(e) => setForm({ ...form, notes_base: e.target.value })} className={`sm:col-span-2 ${inp}`} />
        <textarea placeholder="Descriere (opțional)" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className={`sm:col-span-2 ${inp} resize-none`} />
        <div className="sm:col-span-2 space-y-3">
          <p className="text-sm text-ink-soft">Galerie imagini <span className="text-ink-muted">— prima poză este cea principală (afișată în catalog). Încarcă față / spate / lateral.</span></p>
          <div className="flex flex-wrap gap-3">
            {form.images.map((src, i) => (
              <div key={src} data-testid={`gallery-img-${i}`} className="relative w-20 h-24 border border-line group overflow-hidden">
                <img src={src} alt="" className="w-full h-full object-cover" />
                {i === 0 && <span className="absolute top-0 left-0 bg-ink text-cream text-[8px] px-1.5 py-0.5 uppercase tracking-wider">Principală</span>}
                <button type="button" data-testid={`gallery-remove-${i}`} onClick={() => removeImage(i)} className="absolute top-0 right-0 bg-ink/80 text-cream w-5 h-5 flex items-center justify-center hover:bg-ink"><X size={11} /></button>
                {i !== 0 && <button type="button" data-testid={`gallery-primary-${i}`} onClick={() => makePrimary(i)} className="absolute bottom-0 inset-x-0 bg-cream/95 text-ink text-[9px] py-0.5 opacity-0 group-hover:opacity-100 transition-opacity">Fă principală</button>}
              </div>
            ))}
            <label className="w-20 h-24 flex flex-col items-center justify-center gap-1 border border-dashed border-line cursor-pointer hover:bg-beige text-[10px] text-ink-soft text-center">
              <Upload size={16} /> {uploading ? "Se încarcă..." : "Adaugă poze"}
              <input data-testid="prod-image-upload" type="file" accept="image/*" multiple onChange={upload} className="hidden" />
            </label>
          </div>
        </div>
        <div className="sm:col-span-2 flex gap-3">
          <button data-testid="prod-save-btn" type="submit" className="px-8 py-3 bg-ink text-cream text-xs uppercase tracking-[0.2em] hover:bg-mocha-dark transition-colors">{editing ? "Salvează" : "Adaugă"}</button>
          {editing && <button type="button" onClick={reset} className="px-6 py-3 border border-line text-xs uppercase tracking-[0.2em] hover:bg-beige">Anulează</button>}
        </div>
      </form>

      <div className="bg-surface border border-line overflow-x-auto">
        <table className="w-full text-sm min-w-[620px]">
          <thead><tr className="text-left eyebrow text-[0.55rem] border-b border-line"><th className="p-3">Produs</th><th className="p-3">Preț</th><th className="p-3">Stoc</th><th className="p-3">Inițial</th><th className="p-3">Status</th><th className="p-3">Acțiuni</th></tr></thead>
          <tbody>
            {list.map((p) => {
              const st = statusOf(p, threshold);
              const out = (p.stock ?? 0) <= 0;
              return (
                <tr key={p.id} data-testid={`admin-prod-${p.id}`} className={`border-b border-line/60 ${out ? "bg-[#FBEAE8]" : ""}`}>
                  <td className="p-3 flex items-center gap-3">
                    <div className="w-9 h-11 bg-beige overflow-hidden shrink-0">{p.image && <img src={p.image} alt="" className="w-full h-full object-cover" />}</div>
                    <div><p className="text-ink font-medium leading-tight">{p.name}</p><p className="text-[11px] text-ink-muted">{p.brand} · {p.category}</p></div>
                  </td>
                  <td className="p-3 whitespace-nowrap">{formatPrice(p.price)}</td>
                  <td className="p-3">{p.stock}</td>
                  <td className="p-3 text-ink-muted">{p.initial_stock}</td>
                  <td className="p-3 whitespace-nowrap">
                    {out ? (
                      <span className="inline-flex items-center gap-1 bg-[#C1362F] text-cream text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded-full"><AlertTriangle size={11} /> Epuizat</span>
                    ) : (
                      <span className={`font-semibold ${st.cls}`}>{st.label}</span>
                    )}
                  </td>
                  <td className="p-3">
                    <div className="flex gap-2">
                      <button onClick={() => edit(p)} className="flex items-center gap-1 px-3 py-1.5 border border-line text-xs hover:bg-beige"><Pencil size={12} /> Editează</button>
                      <button data-testid={`admin-del-${p.id}`} onClick={() => remove(p.id)} className="px-2 py-1.5 border border-line text-destructive hover:bg-beige"><Trash2 size={13} /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Sales() {
  const [list, setList] = useState([]);
  const [form, setForm] = useState({ product_name: "", quantity: 1, price: "", date: new Date().toISOString().slice(0, 10) });
  const load = useCallback(() => axios.get(`${API}/admin/sales`, { headers: authHeaders() }).then((r) => setList(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const save = async (e) => {
    e.preventDefault();
    if (!form.product_name || !form.price) { toast.error("Produs și preț obligatorii"); return; }
    await axios.post(`${API}/admin/sales`, { product_name: form.product_name, quantity: parseInt(form.quantity) || 1, price: parseFloat(form.price), date: form.date ? new Date(form.date).toISOString() : null }, { headers: authHeaders() });
    toast.success("Vânzare înregistrată"); setForm({ product_name: "", quantity: 1, price: "", date: new Date().toISOString().slice(0, 10) }); load();
  };
  const remove = async (id) => { await axios.delete(`${API}/admin/sales/${id}`, { headers: authHeaders() }); load(); };
  return (
    <div data-testid="admin-sales" className="space-y-8">
      <form onSubmit={save} className="bg-surface border border-line p-6 grid grid-cols-1 sm:grid-cols-4 gap-4">
        <p className="sm:col-span-4 font-serif text-2xl text-ink">Înregistrează o vânzare</p>
        <input data-testid="sale-name" placeholder="Nume produs" value={form.product_name} onChange={(e) => setForm({ ...form, product_name: e.target.value })} className="sm:col-span-2 border border-line bg-cream px-4 py-3 outline-none focus:border-mocha-dark" />
        <input data-testid="sale-qty" type="number" placeholder="Cantitate" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} className="border border-line bg-cream px-4 py-3 outline-none focus:border-mocha-dark" />
        <input data-testid="sale-price" type="number" placeholder="Preț (RON)" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="border border-line bg-cream px-4 py-3 outline-none focus:border-mocha-dark" />
        <input data-testid="sale-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="border border-line bg-cream px-4 py-3 outline-none focus:border-mocha-dark" />
        <button data-testid="sale-save-btn" className="sm:col-span-3 py-3 bg-ink text-cream text-xs uppercase tracking-[0.2em] hover:bg-mocha-dark transition-colors">Adaugă vânzarea</button>
      </form>
      <div className="bg-surface border border-line overflow-x-auto">
        <table className="w-full text-sm min-w-[520px]">
          <thead><tr className="text-left eyebrow text-[0.55rem] border-b border-line"><th className="p-3">Produs</th><th className="p-3">Cant.</th><th className="p-3">Preț</th><th className="p-3">Total</th><th className="p-3">Dată</th><th></th></tr></thead>
          <tbody>
            {list.map((s) => (
              <tr key={s.id} className="border-b border-line/60">
                <td className="p-3 text-ink">{s.product_name}</td><td className="p-3">{s.quantity}</td><td className="p-3">{formatPrice(s.price)}</td><td className="p-3">{formatPrice(s.price * s.quantity)}</td><td className="p-3 text-ink-muted">{(s.date || "").slice(0, 10)}</td>
                <td className="p-3"><button onClick={() => remove(s.id)} className="text-destructive"><Trash2 size={14} /></button></td>
              </tr>
            ))}
            {list.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-ink-muted">Nicio vânzare înregistrată.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Orders() {
  const [list, setList] = useState([]);
  const [err, setErr] = useState(false);
  const [loading, setLoading] = useState(true);
  const [updatedAt, setUpdatedAt] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/orders`, { headers: authHeaders() });
      setList(r.data); setErr(false); setUpdatedAt(new Date());
    } catch (e) {
      setErr(e?.response?.status === 401 ? "auth" : "net");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 20000);
    const onVis = () => { if (document.visibilityState === "visible") load(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { clearInterval(id); document.removeEventListener("visibilitychange", onVis); };
  }, [load]);

  return (
    <div data-testid="admin-orders" className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="text-sm text-ink-soft">{list.length} comenzi{updatedAt ? ` · actualizat ${updatedAt.toLocaleTimeString("ro-RO", { hour: "2-digit", minute: "2-digit" })}` : ""}</p>
        <button data-testid="orders-refresh-btn" onClick={load} className="flex items-center gap-2 text-xs uppercase tracking-widest border border-line px-4 py-2 hover:bg-surface transition-colors">
          <RefreshCw size={14} /> Reîmprospătează
        </button>
      </div>
      {err === "auth" && <p data-testid="orders-auth-err" className="text-destructive text-sm">Sesiunea a expirat. Reîncarcă pagina și autentifică-te din nou.</p>}
      {err === "net" && <p className="text-destructive text-sm">Nu am putut încărca comenzile. Verifică conexiunea și apasă Reîmprospătează.</p>}
      {!err && !loading && list.length === 0 && <p className="text-ink-muted">Nicio comandă încă.</p>}
      {list.map((o) => (
        <div key={o.id} className="bg-surface border border-line p-5">
          <div className="flex justify-between flex-wrap gap-2">
            <p className="font-serif text-xl text-ink">{o.name}</p>
            <p className="text-mocha-dark font-medium">{formatPrice(o.total != null ? o.total : o.subtotal)}</p>
          </div>
          <p className="text-sm text-ink-soft mt-1">{o.email || "—"} · {o.phone || "—"}</p>
          <p className="text-sm text-ink-soft">{[o.address, o.city, o.county].filter(Boolean).join(", ") || "—"}</p>
          <p className="text-xs text-ink-muted mt-1">Plată: {o.payment_method || "Cash"} · Transport: {o.shipping ? formatPrice(o.shipping) : "Gratuit"} · {(o.created_at || "").slice(0, 10)}</p>
          <p className="text-sm text-ink mt-2">{(o.items || []).map((i) => `${i.product?.name || i.product_id} ×${i.quantity}`).join(", ")}</p>
        </div>
      ))}
    </div>
  );
}

function Contacts() {
  const [list, setList] = useState([]);
  useEffect(() => { axios.get(`${API}/admin/contacts`, { headers: authHeaders() }).then((r) => setList(r.data)).catch(() => {}); }, []);
  return (
    <div data-testid="admin-contacts" className="space-y-4">
      {list.length === 0 && <p className="text-ink-muted">Nicio solicitare încă.</p>}
      {list.map((c) => (
        <div key={c.id} className="bg-surface border border-line p-5">
          <div className="flex justify-between flex-wrap gap-2">
            <p className="font-serif text-xl text-ink">{c.name}</p>
            <p className="text-xs text-ink-muted">{(c.created_at || "").slice(0, 10)}</p>
          </div>
          <p className="text-sm text-mocha-dark">{c.email}</p>
          <p className="text-sm text-ink-soft mt-2">{c.message}</p>
        </div>
      ))}
    </div>
  );
}

const TABS = [
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard, comp: Dashboard },
  { id: "products", label: "Produse", icon: Package, comp: Products },
  { id: "sales", label: "Vânzări", icon: Receipt, comp: Sales },
  { id: "orders", label: "Comenzi", icon: ShoppingCart, comp: Orders },
  { id: "contacts", label: "Solicitări", icon: Mail, comp: Contacts },
];

export default function Admin() {
  const [authed, setAuthed] = useState(null);
  const [tab, setTab] = useState("dashboard");
  const check = useCallback(() => {
    if (!localStorage.getItem(TKEY)) { setAuthed(false); return; }
    axios.get(`${API}/admin/me`, { headers: authHeaders() }).then(() => setAuthed(true)).catch(() => { localStorage.removeItem(TKEY); setAuthed(false); });
  }, []);
  useEffect(() => { check(); }, [check]);

  if (authed === null) return <div className="min-h-screen bg-cream" />;
  if (!authed) return <Login onLogin={() => setAuthed(true)} />;

  const Active = TABS.find((t) => t.id === tab).comp;
  const logout = () => { localStorage.removeItem(TKEY); setAuthed(false); };

  return (
    <div className="min-h-screen bg-cream flex flex-col lg:flex-row">
      <aside className="lg:w-64 bg-footer text-cream lg:min-h-screen p-6 flex lg:flex-col gap-2 overflow-x-auto">
        <div className="hidden lg:block mb-6"><p className="font-serif text-2xl">ORÉLIE</p><p className="eyebrow text-mocha text-[0.55rem]">Administrare</p></div>
        {TABS.map((t) => (
          <button key={t.id} data-testid={`admin-tab-${t.id}`} onClick={() => setTab(t.id)}
            className={`flex items-center gap-3 px-4 py-3 text-sm whitespace-nowrap transition-colors ${tab === t.id ? "bg-mocha text-footer" : "text-cream/80 hover:bg-white/10"}`}>
            <t.icon size={18} strokeWidth={1.5} /> {t.label}
          </button>
        ))}
        <button data-testid="admin-logout" onClick={logout} className="flex items-center gap-3 px-4 py-3 text-sm text-cream/80 hover:bg-white/10 lg:mt-auto whitespace-nowrap"><LogOut size={18} strokeWidth={1.5} /> Ieșire</button>
      </aside>
      <main className="flex-1 p-6 lg:p-10">
        <h1 className="font-serif text-3xl lg:text-4xl text-ink mb-8">{TABS.find((t) => t.id === tab).label}</h1>
        <Active />
      </main>
    </div>
  );
}
